/**
 * CharacterCountPlugin.tsx
 *
 * Displays real-time character count with platform-specific limits.
 * Shows progress bar, warnings when approaching limit, and per-platform status.
 *
 * Features:
 * - Real-time character counting with proper emoji support
 * - Platform-specific counting rules (Twitter counts emojis as 2 characters)
 * - Platform-specific limits from centralized constants
 * - Progress bar with color coding (green 0-80%, yellow 80-95%, red 95%+)
 * - Per-platform status (over/under limit)
 * - Most restrictive limit display
 * - Visual feedback with icons
 *
 * Character Counting:
 * - Uses Intl.Segmenter for accurate grapheme cluster counting
 * - "Hello face" = 7 characters (not 8 from string.length)
 * - "family" = 1 character (not 11 from string.length)
 * - Twitter/X: emojis count as 2 characters each
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import {
  checkLimitsWithCounting,
  calculatePercentage,
  getPercentageColorClass,
  getProgressBarColorClass,
  formatNumber,
  getPlatformLimit,
  getPlatformDisplayName,
  type LimitCheckResult,
} from '@/constants/platformLimits';
import {
  countCharacters,
  countCharactersForPlatform,
  countWords,
} from '@/utils/characterCount';
import { countHashtags } from '@/utils/hashtags';

// Re-export for backwards compatibility
export { PLATFORM_LIMITS } from '@/constants/platformLimits';

interface CharacterCountPluginProps {
  /**
   * Selected platforms to show counts for
   * If not provided, shows generic count without platform-specific limits
   */
  platforms?: string[];

  /**
   * Maximum character limit (used if platforms not provided)
   */
  maxLength?: number;

  /**
   * Callback when character count changes
   */
  onChange?: (count: number) => void;

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Show compact version (only most restrictive limit)
   */
  compact?: boolean;

  /**
   * Show progress bar
   */
  showProgressBar?: boolean;

  /**
   * Show per-platform breakdown
   */
  showPlatformBreakdown?: boolean;
}

/**
 * Progress bar component showing character usage
 */
function ProgressBar({ percentage, className = '' }: { percentage: number; className?: string }) {
  const colorClass = getProgressBarColorClass(percentage);
  const clampedPercentage = Math.min(percentage, 100);

  return (
    <div className={`h-1.5 w-full bg-gray-200 rounded-full overflow-hidden ${className}`}>
      <div
        className={`h-full transition-all duration-200 ${colorClass}`}
        style={{ width: `${clampedPercentage}%` }}
      />
    </div>
  );
}

/**
 * Platform status indicator component
 * Uses platform-specific character counting for accurate limit checks
 */
function PlatformStatus({
  platform,
  text,
  isCompact = false,
}: {
  platform: string;
  text: string;
  isCompact?: boolean;
}) {
  const limit = getPlatformLimit(platform, 'characters');
  const displayName = getPlatformDisplayName(platform);
  // Use platform-specific counting (e.g., Twitter counts emojis as 2)
  const platformCount = countCharactersForPlatform(text, platform);
  const percentage = calculatePercentage(platformCount, limit);
  const isOver = platformCount > limit;
  const colorClass = getPercentageColorClass(percentage);

  if (isCompact) {
    return (
      <span className={`text-xs ${colorClass}`}>
        {displayName}: {formatNumber(platformCount)}/{formatNumber(limit)}
        {isOver && ` (+${formatNumber(platformCount - limit)})`}
      </span>
    );
  }

  return (
    <div className="flex items-center justify-between text-sm py-1">
      <div className="flex items-center gap-2">
        {isOver ? (
          <Icon name={ICONS.CAUTION} size="sm" color="text-danger" />
        ) : (
          <Icon name={ICONS.TICK_CIRCLE} size="sm" color="text-success" />
        )}
        <span className="font-medium text-gray-700">{displayName}</span>
      </div>
      <span className={colorClass}>
        {formatNumber(platformCount)} / {formatNumber(limit)}
        {isOver && (
          <span className="ml-1 text-danger font-semibold">
            (+{formatNumber(platformCount - limit)})
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * CharacterCountPlugin component
 * Displays character count below the editor with platform-specific limits
 */
export function CharacterCountPlugin({
  platforms = [],
  maxLength,
  onChange,
  className = '',
  compact = false,
  showProgressBar = true,
  showPlatformBreakdown = true,
}: CharacterCountPluginProps): JSX.Element | null {
  const { t } = useTranslation('common');
  const [editor] = useLexicalComposerContext();
  // Character count (grapheme-aware, for display)
  const [characterCount, setCharacterCount] = useState(0);
  // Word count state
  const [wordCount, setWordCount] = useState(0);
  // Hashtag count state
  const [hashtagCount, setHashtagCount] = useState(0);
  // Raw text content (needed for platform-specific counting)
  const [textContent, setTextContent] = useState('');

  // Subscribe to editor updates
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const text = root.getTextContent();

        // Use grapheme-aware counting (accurate for emojis)
        const count = countCharacters(text);
        const words = countWords(text);
        const hashtags = countHashtags(text);

        setTextContent(text);
        setCharacterCount(count);
        setWordCount(words);
        setHashtagCount(hashtags);

        if (onChange) {
          onChange(count);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Calculate limit check results using platform-specific counting
  const limitCheck: LimitCheckResult = useMemo(() => {
    return checkLimitsWithCounting(textContent, platforms, 'characters');
  }, [textContent, platforms]);

  // If platforms are selected, show platform-aware UI
  if (platforms.length > 0) {
    // Hide stats when caption is empty
    if (characterCount === 0) {
      return null;
    }

    const { mostRestrictiveLimit, mostRestrictivePlatform, overLimits, withinLimits, isWithinAll } = limitCheck;
    const percentage = calculatePercentage(characterCount, mostRestrictiveLimit);
    const percentageColorClass = getPercentageColorClass(percentage);

    return (
      <div className={`mt-3 space-y-3 ${className}`}>
        {/* Main counter with most restrictive limit and word count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Character count with status icon */}
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${percentageColorClass}`}>
                {formatNumber(characterCount)} / {formatNumber(mostRestrictiveLimit)}
              </span>
              {!isWithinAll && (
                <Icon name={ICONS.CAUTION} size="sm" color="text-danger" />
              )}
              {isWithinAll && characterCount > 0 && (
                <Icon name={ICONS.TICK_CIRCLE} size="sm" color="text-success" />
              )}
            </div>
            {/* Word count */}
            <span className="text-sm text-gray-500">
              {wordCount.toLocaleString()} {wordCount === 1 ? t('word') : t('words')}
            </span>
            {/* Hashtag count */}
            {hashtagCount > 0 && (
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Icon name={ICONS.TAG} size="xs" />
                {hashtagCount} {hashtagCount === 1 ? t('hashtag') : t('hashtags')}
              </span>
            )}
          </div>
          {platforms.length > 1 && (
            <span className="text-xs text-gray-500">
              {t('based_on_platform', { platform: getPlatformDisplayName(mostRestrictivePlatform) })}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {showProgressBar && <ProgressBar percentage={percentage} />}

        {/* Platform breakdown - warnings first, then success */}
        {showPlatformBreakdown && !compact && (
          <div className="space-y-1 pt-2 border-t border-gray-100">
            {/* Over limit warnings */}
            {overLimits.length > 0 && (
              <div className="space-y-1">
                {overLimits.map(({ platform, limit, displayName, overBy }) => (
                  <div
                    key={platform}
                    className="flex items-center gap-2 text-sm text-danger"
                  >
                    <Icon name={ICONS.CAUTION} size="sm" color="text-danger" />
                    <span>
                      {t('over_limit_for_platform', {
                        platform: displayName,
                        limit: formatNumber(limit),
                        overBy: formatNumber(overBy)
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Within limit success */}
            {withinLimits.length > 0 && characterCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-success">
                <Icon name={ICONS.TICK_CIRCLE} size="sm" color="text-success" />
                <span>
                  {t('within_limit_for', {
                    platforms: withinLimits
                      .map(({ displayName, limit }) => `${displayName} (${formatNumber(limit)})`)
                      .join(', ')
                  })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Compact platform breakdown */}
        {showPlatformBreakdown && compact && platforms.length > 1 && (
          <div className="flex flex-wrap gap-2 text-xs">
            {platforms.map((platform) => (
              <PlatformStatus
                key={platform}
                platform={platform}
                text={textContent}
                isCompact
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Generic count (no platforms selected)
  // Hide stats when caption is empty
  if (characterCount === 0) {
    return null;
  }

  const limit = maxLength || 3000;
  const percentage = calculatePercentage(characterCount, limit);
  const percentageColorClass = getPercentageColorClass(percentage);
  const isOver = characterCount > limit;

  return (
    <div className={`mt-3 space-y-2 ${className}`}>
      {/* Counter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Character count with status icon */}
          <div className="flex items-center gap-2">
            <span className={`text-sm ${percentageColorClass}`}>
              {formatNumber(characterCount)}
              {maxLength && ` / ${formatNumber(maxLength)}`}
            </span>
            {isOver && <Icon name={ICONS.CAUTION} size="sm" color="text-danger" />}
            {!isOver && characterCount > 0 && (
              <Icon name={ICONS.TICK_CIRCLE} size="sm" color="text-success" />
            )}
          </div>
          {/* Word count */}
          <span className="text-sm text-gray-500">
            {wordCount.toLocaleString()} {wordCount === 1 ? t('word') : t('words')}
          </span>
          {/* Hashtag count */}
          {hashtagCount > 0 && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Icon name={ICONS.TAG} size="xs" />
              {hashtagCount} {hashtagCount === 1 ? t('hashtag') : t('hashtags')}
            </span>
          )}
        </div>
        {isOver && maxLength && (
          <span className="text-xs text-danger font-medium">
            {t('character_count_over', { count: characterCount - maxLength })}
          </span>
        )}
      </div>

      {/* Progress bar */}
      {showProgressBar && maxLength && <ProgressBar percentage={percentage} />}
    </div>
  );
}

export default CharacterCountPlugin;
